(() => {
  if (!window.customElements || customElements.get('tm-work-viewer')) return;

  class ImageCarousel {
    constructor(root, onChange = () => {}) {
      this.root = root;
      this.track = root.querySelector('[data-carousel-track]');
      this.slides = [...this.track.querySelectorAll('[data-carousel-slide]')];
      this.previous = root.querySelector('[data-carousel-previous]');
      this.next = root.querySelector('[data-carousel-next]');
      this.count = root.querySelector('[data-carousel-count]');
      this.thumbs = [...root.querySelectorAll('[data-carousel-thumb]')];
      this.onChange = onChange;
      this.index = 0;
      this.controller = new AbortController();
      const on = (el, type, fn, extra = {}) => el?.addEventListener(type, fn, { signal: this.controller.signal, ...extra });
      this.previous.disabled = this.next.disabled = this.slides.length < 2;
      root.querySelector('[data-carousel-controls]').hidden = false;
      this.track.tabIndex = 0;
      on(this.previous, 'click', () => this.go(this.index - 1));
      on(this.next, 'click', () => this.go(this.index + 1));
      this.thumbs.forEach((button, index) => on(button, 'click', () => this.go(index)));
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.readPosition());
      }, { passive: true });
      on(this.track, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); event.stopPropagation();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      on(this.track, 'pointerdown', event => {
        if (event.button !== 0) return;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, mouse: event.pointerType === 'mouse', dragged: false };
      });
      on(this.track, 'pointermove', event => {
        const g = this.gesture;
        if (!g || g.id !== event.pointerId) return;
        const dx = event.clientX - g.x, dy = event.clientY - g.y;
        if (!g.dragged && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          g.dragged = true;
          if (g.mouse) {
            this.track.setPointerCapture?.(g.id);
            this.track.classList.add('is-dragging');
          }
        }
        if (g.dragged && g.mouse) {
          event.preventDefault();
          this.track.scrollLeft = g.left - dx;
        }
      });
      const endGesture = event => {
        const g = this.gesture;
        if (!g || g.id !== event.pointerId) return;
        if (g.dragged || event.type === 'pointercancel') this.suppressUntil = Date.now() + 350;
        this.track.classList.remove('is-dragging');
        if (g.dragged && g.mouse && this.track.clientWidth) this.go(Math.round(this.track.scrollLeft / this.track.clientWidth));
        this.gesture = null;
      };
      on(this.track, 'pointerup', endGesture);
      on(this.track, 'pointercancel', endGesture);
      on(this.track, 'click', event => {
        if (event.detail !== 0 && Date.now() < (this.suppressUntil || 0)) {
          event.preventDefault(); event.stopPropagation();
        }
      }, { capture: true });
      on(this.track, 'dragstart', event => event.preventDefault());
      if (window.ResizeObserver) {
        this.resize = new ResizeObserver(() => this.go(this.index));
        this.resize.observe(this.track);
      }
      this.update(0);
    }

    readPosition() {
      if (this.track.clientWidth) this.update(Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / this.track.clientWidth))));
    }

    go(index) {
      const next = (index + this.slides.length) % this.slides.length;
      this.update(next);
      // Native touch/trackpad scrolling stays fluid; arrow clicks land on an exact image.
      this.track.scrollTo({ left: next * this.track.clientWidth, behavior: 'auto' });
    }

    update(index) {
      this.index = index;
      this.count.textContent = `${index + 1} / ${this.slides.length}`;
      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        if (slide.matches('a')) slide.tabIndex = i === index ? 0 : -1;
      });
      this.thumbs.forEach((button, i) => button.setAttribute('aria-pressed', i === index ? 'true' : 'false'));
      this.onChange(index);
    }

    destroy() {
      this.controller.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.track.classList.remove('is-dragging');
      this.slides.forEach(slide => { slide.removeAttribute('aria-hidden'); slide.removeAttribute('tabindex'); });
      this.track.removeAttribute('tabindex');
    }
  }

  class ProjectCard extends HTMLElement {
    connectedCallback() { if (!this.carousel) this.carousel = new ImageCarousel(this); }
    disconnectedCallback() { this.carousel?.destroy(); this.carousel = null; }
  }
  customElements.define('tm-project-card', ProjectCard);

  class WorkViewer extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('[data-work-dialog]');
      this.allCards = [...this.querySelectorAll('tm-project-card')];
      this.cards = this.allCards;
      if (!this.dialog || !this.cards.length || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const on = (el, type, fn) => el.addEventListener(type, fn, { signal: this.controller.signal });
      this.filterControls = this.querySelector('[data-work-filters]');
      if (this.filterControls) {
        this.filterButtons = [...this.filterControls.querySelectorAll('[data-work-filter]')];
        this.filterButtons.forEach(button => {
          const key = button.dataset.workFilter;
          button.querySelector('[data-work-total]').textContent = this.matchingCards(key).length;
          on(button, 'click', () => this.applyFilter(key, true));
        });
        this.applyFilter(new URL(location.href).searchParams.get('work') || 'all');
        this.filterControls.hidden = false;
      }
      this.closeButton = this.dialog.querySelector('[data-work-close]');
      this.gallery = this.dialog.querySelector('[data-project-gallery]');
      this.track = this.gallery.querySelector('[data-carousel-track]');
      this.status = this.dialog.querySelector('[data-work-status]');
      this.allCards.forEach(card => card.querySelectorAll('[data-project-open]').forEach(link => {
        link.setAttribute('aria-haspopup', 'dialog'); link.setAttribute('aria-controls', this.dialog.id);
      }));
      on(this, 'click', event => {
        const link = event.target.closest('[data-project-open]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const card = link.closest('tm-project-card');
        const index = this.cards.indexOf(card);
        if (index < 0) return;
        const slides = [...card.querySelectorAll('[data-carousel-slide]')];
        const imageIndex = link.hasAttribute('data-use-active') ? (card.carousel?.index || 0) : Math.max(0, slides.indexOf(link));
        this.opener = link;
        this.renderProject(index, imageIndex);
        try { this.dialog.showModal(); } catch (_) { this.opener = null; return; }
        event.preventDefault();
        this.lockScroll();
        this.carousel.go(imageIndex);
        this.closeButton.focus({ preventScroll: true });
      });
      on(this.closeButton, 'click', () => this.closeViewer());
      on(this.dialog.querySelector('.tm-project-dialog__cta'), 'click', () => this.closeViewer());
      on(this.dialog.querySelector('[data-project-previous]'), 'click', () => this.renderProject(this.projectIndex - 1));
      on(this.dialog.querySelector('[data-project-next]'), 'click', () => this.renderProject(this.projectIndex + 1));
      on(this.dialog, 'keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.carousel.go(this.carousel.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.closeViewer(); });
      on(this.dialog, 'close', () => this.finishClose());
      const openHash = () => {
        const match = /^#project-([a-z0-9-]+)$/.exec(location.hash);
        if (!match) { if (this.fromHash && this.dialog.open) this.closeViewer(); return; }
        const target = this.allCards.find(card => card.dataset.projectId === match[1]);
        if (target && !this.cards.includes(target)) this.applyFilter('all', true, true);
        const index = this.cards.indexOf(target);
        if (index < 0) return;
        this.opener = this.cards[index].querySelector('[data-project-open][data-use-active]');
        this.renderProject(index, 0);
        if (!this.dialog.open) { try { this.dialog.showModal(); } catch (_) { return; } }
        this.fromHash = true; this.lockScroll(); this.carousel.go(0); this.closeButton.focus({ preventScroll: true });
      };
      // History traversal can fire popstate before hashchange. Keep the incoming
      // project hash while changing filters, then open the requested project.
      const syncLocation = () => {
        const requested = new URL(location.href).searchParams.get('work') || 'all';
        const key = this.filterButtons?.some(button => button.dataset.workFilter === requested) ? requested : 'all';
        if (this.filterControls && key !== this.activeFilter) this.applyFilter(key, false, true);
        openHash();
      };
      on(window, 'popstate', syncLocation);
      on(window, 'hashchange', syncLocation);
      queueMicrotask(() => { if (this.isConnected) syncLocation(); });
      let backdropDown = false;
      on(this.dialog, 'pointerdown', event => { backdropDown = event.target === this.dialog; });
      on(this.dialog, 'click', event => {
        if (backdropDown && event.target === this.dialog) this.closeViewer();
        backdropDown = false;
      });
    }

    matchingCards(key) {
      return key === 'all' ? this.allCards : this.allCards.filter(card => card.dataset.workTypes?.split(' ').includes(key));
    }

    applyFilter(key, updateUrl = false, preserveHash = false) {
      if (!this.filterControls) return;
      if (!this.filterButtons.some(button => button.dataset.workFilter === key)) key = 'all';
      if (this.dialog.open) {
        if (preserveHash) this.fromHash = false;
        this.closeViewer();
      }
      this.activeFilter = key;
      this.cards = this.matchingCards(key);
      this.allCards.forEach(card => { card.hidden = !this.cards.includes(card); });
      this.filterButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.workFilter === key)));
      const result = this.querySelector('[data-work-result]');
      result.textContent = key === 'all' ? this.cards.length + ' projects · 12 brands' : this.cards.length + ' projects';
      if (updateUrl) {
        const url = new URL(location.href);
        if (key === 'all') url.searchParams.delete('work'); else url.searchParams.set('work', key);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
    }

    renderProject(index, imageIndex = 0) {
      this.projectIndex = (index + this.cards.length) % this.cards.length;
      const card = this.cards[this.projectIndex];
      this.carousel?.destroy();
      this.carousel = null;
      this.projectController?.abort();
      this.projectController = new AbortController();
      for (const field of ['title', 'category', 'summary', 'products', 'details']) {
        this.dialog.querySelector(`[data-project-${field}]`).textContent = card.dataset[field];
      }
      this.dialog.querySelector('[data-project-count]').textContent = `Project ${this.projectIndex + 1} of ${this.cards.length}`;
      const thumbs = this.gallery.querySelector('[data-carousel-thumbs]');
      this.track.replaceChildren(); thumbs.replaceChildren();
      const sources = [...card.querySelectorAll('[data-carousel-slide] img')];
      sources.forEach((source, i) => {
        const slide = document.createElement('div');
        slide.className = 'tm-project-dialog__slide'; slide.dataset.carouselSlide = '';
        slide.setAttribute('role', 'group'); slide.setAttribute('aria-label', `Image ${i + 1} of ${sources.length}`);
        const image = source.cloneNode(false);
        image.loading = 'eager'; image.draggable = false;
        for (const type of ['load', 'error']) image.addEventListener(type, () => this.imageStatus(), { signal: this.projectController.signal });
        slide.append(image); this.track.append(slide);
        const thumb = document.createElement('button');
        thumb.type = 'button'; thumb.dataset.carouselThumb = '';
        thumb.setAttribute('aria-label', `Show image ${i + 1}: ${source.alt}`);
        const tiny = source.cloneNode(false); tiny.alt = ''; tiny.loading = 'eager'; tiny.draggable = false;
        thumb.append(tiny); thumbs.append(thumb);
      });
      this.carousel = new ImageCarousel(this.gallery, () => this.imageStatus());
      this.carousel.go(imageIndex);
      for (const [direction, offset] of [['previous', -1], ['next', 1]]) {
        const target = this.cards[(this.projectIndex + offset + this.cards.length) % this.cards.length];
        const button = this.dialog.querySelector(`[data-project-${direction}]`);
        button.disabled = this.cards.length < 2;
        button.setAttribute('aria-label', `${direction === 'next' ? 'Next' : 'Previous'} project: ${target.dataset.title}`);
        button.querySelector('[data-project-name]').textContent = target.dataset.title;
      }
      this.dialog.querySelector('[data-project-body]').scrollTop = 0;
      this.dialog.querySelector('.tm-project-dialog__info').scrollTop = 0;
    }

    imageStatus() {
      const index = this.carousel?.index || 0;
      const image = this.track.querySelectorAll('img')[index];
      if (!image) return;
      const loaded = image.complete && image.naturalWidth > 0;
      this.status.hidden = loaded;
      this.status.textContent = image.complete && !image.naturalWidth ? 'This image could not be loaded. Try another image.' : 'Loading image…';
      this.track.setAttribute('aria-busy', image.complete ? 'false' : 'true');
      this.dialog.querySelector('[data-image-caption]').textContent = /[.!?]$/.test(image.alt) ? image.alt : image.alt + '.';
    }

    closeViewer() { this.dialog.close(); this.finishClose(); }

    lockScroll() {
      if (this.savedScroll) return;
      const body = document.body, root = document.documentElement;
      this.savedScroll = { x: scrollX, y: scrollY, body: Object.fromEntries(['position','top','left','width','overflow','paddingRight'].map(key => [key, body.style[key]])), overflow: root.style.overflow, behavior: root.style.scrollBehavior };
      const gap = Math.max(0, innerWidth - root.clientWidth);
      if (gap) body.style.paddingRight = `${(parseFloat(getComputedStyle(body).paddingRight) || 0) + gap}px`;
      Object.assign(body.style, { position: 'fixed', top: `${-this.savedScroll.y}px`, left: `${-this.savedScroll.x}px`, width: '100%', overflow: 'hidden' });
      root.style.overflow = 'hidden';
    }

    unlockScroll() {
      const saved = this.savedScroll;
      if (!saved) return;
      this.savedScroll = null;
      Object.assign(document.body.style, saved.body);
      document.documentElement.style.overflow = saved.overflow;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(saved.x, saved.y);
      document.documentElement.style.scrollBehavior = saved.behavior;
    }

    finishClose() {
      if (this.dialog.open) return;
      this.unlockScroll();
      if (this.fromHash) { if (/^#project-/.test(location.hash)) history.replaceState(history.state, '', location.pathname + location.search); this.fromHash = false; }
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.closeViewer();
      this.unlockScroll(); this.carousel?.destroy(); this.projectController?.abort(); this.controller?.abort();
      this.controller = null; this.opener = null;
    }
  }
  customElements.define('tm-work-viewer', WorkViewer);
})();
